import type {
  DraftCostpointAssignment,
  DraftTransaction,
  ImportDraft,
  SplitTransactionRequest,
  Tag,
} from '@finapp/types';
import { calculateNetAmount, calculateVatAmount } from '@finapp/types';
import React, { useCallback, useEffect, useState } from 'react';
import { apiUrl } from '../config/api';
import { type BulkEditData, BulkEditModal } from './BulkEditModal';
import { DraftProgressBar } from './DraftProgressBar';
import { DraftTransactionItem } from './DraftTransactionItem';

interface DraftTransactionListProps {
  draft: ImportDraft;
  onUpdateTransaction: (transactionId: string, updates: Partial<DraftTransaction>) => void;
  onSaveDraft: () => void;
  onValidateDraft: () => void;
  onPartialCommitDraft: () => void;
  onCommitDraft: () => void;
  onAbandonDraft: () => void;
  onRefreshDraft?: () => void;
  isLoading?: boolean;
}

const DraftTransactionListComponent: React.FC<DraftTransactionListProps> = ({
  draft,
  onUpdateTransaction,
  onSaveDraft,
  onValidateDraft,
  onPartialCommitDraft,
  onCommitDraft,
  onAbandonDraft,
  onRefreshDraft,
  isLoading = false,
}) => {
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);

  // Multi-select state for bulk operations
  const [selectedTransactions, setSelectedTransactions] = useState<Set<string>>(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [bulkEditing, setBulkEditing] = useState(false);

  // Multi-select handlers
  const handleSelectTransaction = (transactionId: string) => {
    const newSelected = new Set(selectedTransactions);
    if (newSelected.has(transactionId)) {
      newSelected.delete(transactionId);
    } else {
      newSelected.add(transactionId);
    }
    setSelectedTransactions(newSelected);
    setShowBulkActions(newSelected.size > 0);
  };

  const handleSelectAll = () => {
    if (selectedTransactions.size === draft.transactions.length) {
      // Deselect all
      setSelectedTransactions(new Set());
      setShowBulkActions(false);
    } else {
      // Select all
      const allIds = new Set(draft.transactions.map((t) => t.id));
      setSelectedTransactions(allIds);
      setShowBulkActions(true);
    }
  };

  const handleClearSelection = () => {
    setSelectedTransactions(new Set());
    setShowBulkActions(false);
  };

  // Split transaction handler
  const handleSplitTransaction = async (transactionId: string, splits: SplitTransactionRequest) => {
    try {
      const response = await fetch(
        apiUrl(`import-drafts/${draft.id}/transactions/${transactionId}/split`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(splits),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to split transaction');
      }

      // Refresh the draft to show the split transactions
      if (onRefreshDraft) {
        onRefreshDraft();
      }
    } catch (error) {
      console.error('Failed to split transaction:', error);
      throw error;
    }
  };

  // Unified bulk edit handler for draft transactions
  const handleBulkEdit = async (data: BulkEditData) => {
    setBulkEditing(true);
    try {
      // Fetch real tag data if we need to add tags
      let realTags: Tag[] = [];
      if (data.tagIds && data.tagIds.length > 0) {
        try {
          const response = await fetch(apiUrl('tags'), {
            credentials: 'include',
          });
          if (response.ok) {
            const result = await response.json();
            if (result.success) {
              realTags = result.data.filter((tag: { id: number }) => data.tagIds?.includes(tag.id));
            }
          }
        } catch (error) {
          console.error('Error fetching tags for bulk edit:', error);
        }
      }

      // Process each selected transaction sequentially to avoid race conditions
      for (const transactionId of selectedTransactions) {
        const transaction = draft.transactions.find((t) => t.id === transactionId);
        if (!transaction) continue;

        // Handle costpoint / tag changes — only when something actually changed.
        // Sending an unchanged costpoints payload to the costpoints endpoint would
        // race against a subsequent VAT call and could overwrite Redis with stale data.
        const hasCostpointChange = !!data.costpoint;
        const hasTagChange = !!data.tagIds && data.tagIds.length > 0 && !!data.tagOperation;
        const hasCostpoints = transaction.costpoints.length > 0;

        if (hasCostpointChange || (hasTagChange && hasCostpoints)) {
          let updatedCostpoints = [...transaction.costpoints];

          if (hasCostpointChange) {
            updatedCostpoints = [
              {
                costpoint: data.costpoint!,
                amount: data.costpointAmount || Math.abs(transaction.amount),
                percentage: 100,
                tags: [],
              },
            ];
          }

          if (hasTagChange) {
            updatedCostpoints = updatedCostpoints.map((cp) => {
              let newTags = [...cp.tags];
              switch (data.tagOperation) {
                case 'add':
                  realTags.forEach((realTag) => {
                    if (!newTags.some((tag) => tag.id === realTag.id)) newTags.push(realTag);
                  });
                  break;
                case 'remove':
                  newTags = newTags.filter((tag) => !data.tagIds?.includes(tag.id));
                  break;
                case 'replace':
                  newTags = realTags;
                  break;
              }
              return { ...cp, tags: newTags };
            });
          }

          await new Promise<void>((resolve) => {
            onUpdateTransaction(transactionId, {
              costpoints: updatedCostpoints,
              is_complete: hasCostpointChange ? true : transaction.is_complete,
            });
            setTimeout(resolve, 100);
          });
        } else if (hasTagChange && !hasCostpoints) {
          // No costpoints yet — apply tags at the transaction level directly
          let newTags = [...(transaction.tags || [])];
          switch (data.tagOperation) {
            case 'add':
              realTags.forEach((realTag) => {
                if (!newTags.some((tag) => tag.id === realTag.id)) newTags.push(realTag);
              });
              break;
            case 'remove':
              newTags = newTags.filter((tag) => !data.tagIds?.includes(tag.id));
              break;
            case 'replace':
              newTags = realTags;
              break;
          }
          await new Promise<void>((resolve) => {
            onUpdateTransaction(transactionId, { tags: newTags });
            setTimeout(resolve, 100);
          });
        }

        // Handle VAT — separate sequential call so it never races the costpoints endpoint.
        if (data.hasVat !== undefined) {
          const vatRate = data.hasVat ? (data.vatRate ?? 0) : 0;
          const getVatIdFromRate = (rate: number): number | null => {
            if (!data.hasVat) return null;
            switch (rate) {
              case 25.5:
                return 2;
              case 14.0:
                return 3;
              case 10.0:
                return 4;
              case 0.0:
                return 1;
              default:
                return null;
            }
          };
          await new Promise<void>((resolve) => {
            onUpdateTransaction(transactionId, {
              has_vat: data.hasVat!,
              vat_id: getVatIdFromRate(vatRate),
              net_amount: calculateNetAmount(transaction.amount, data.hasVat ?? false, vatRate),
              vat_amount: calculateVatAmount(transaction.amount, data.hasVat ?? false, vatRate),
            });
            setTimeout(resolve, 100);
          });
        }
      }

      // Clear selection
      handleClearSelection();

      // For bulk operations, refresh the draft to show all updated items
      // This ensures validation summaries and computed fields are updated
      console.log('Bulk edit completed - refreshing draft to show all changes');
      if (onRefreshDraft) {
        setTimeout(() => {
          onRefreshDraft();
        }, 500); // Small delay to allow individual updates to complete
      }
    } catch (error) {
      console.error('Error in bulk edit:', error);
    } finally {
      setBulkEditing(false);
    }
  };

  // Auto-save functionality - save every 5 minutes
  useEffect(() => {
    if (autoSaveEnabled && draft.status === 'draft') {
      const timeoutId = setTimeout(
        () => {
          onSaveDraft();
        },
        5 * 60 * 1000
      ); // Auto-save after 5 minutes of inactivity

      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [draft, autoSaveEnabled, onSaveDraft]);

  // Helper function to detect if costpoint changes are structural (not just tag updates)
  const hasStructuralCostpointChanges = useCallback(
    (newCostpoints?: DraftCostpointAssignment[]) => {
      console.log(
        '[DraftTransactionList] Checking structural changes for costpoints:',
        newCostpoints
      );

      if (!newCostpoints) return false;

      // Find the current transaction to compare against
      const currentTransaction = draft.transactions.find(
        (t) => selectedTransactionId && t.id === selectedTransactionId
      );

      console.log(
        '[DraftTransactionList] Current transaction:',
        currentTransaction?.id,
        'Selected:',
        selectedTransactionId
      );

      if (!currentTransaction) {
        console.log('[DraftTransactionList] No current transaction found - treating as structural');
        return true; // New costpoints are structural
      }

      const currentCostpoints = currentTransaction.costpoints || [];
      console.log('[DraftTransactionList] Comparing costpoints:', {
        current: currentCostpoints.length,
        new: newCostpoints.length,
      });

      // Check if the structure changed (different costpoints, percentages, or amounts)
      if (newCostpoints.length !== currentCostpoints.length) {
        console.log('[DraftTransactionList] Length changed - structural');
        return true;
      }

      for (let i = 0; i < newCostpoints.length; i++) {
        const newCp = newCostpoints[i];
        const currentCp = currentCostpoints[i];

        if (
          !newCp ||
          !currentCp ||
          newCp.costpoint !== currentCp.costpoint ||
          Math.abs(newCp.percentage - currentCp.percentage) > 0.01 ||
          Math.abs(newCp.amount - currentCp.amount) > 0.01
        ) {
          console.log('[DraftTransactionList] Structural change detected at index', i, {
            newCp: newCp
              ? {
                  costpoint: newCp.costpoint,
                  percentage: newCp.percentage,
                  amount: newCp.amount,
                }
              : null,
            currentCp: currentCp
              ? {
                  costpoint: currentCp.costpoint,
                  percentage: currentCp.percentage,
                  amount: currentCp.amount,
                }
              : null,
          });
          return true;
        }
      }

      console.log('[DraftTransactionList] Only tag changes detected');
      return false; // Only tag changes, no structural changes
    },
    [draft, selectedTransactionId]
  );

  const handleTransactionUpdate = useCallback(
    (transactionId: string, updates: Partial<DraftTransaction>) => {
      console.log('[DraftTransactionList] Transaction update:', {
        transactionId,
        updates,
      });

      onUpdateTransaction(transactionId, updates);

      // Only trigger save for structural changes, not just tag updates
      const isStructuralChange =
        updates.description !== undefined ||
        updates.heading !== undefined ||
        updates.amount !== undefined ||
        updates.has_vat !== undefined ||
        (updates.costpoints !== undefined && hasStructuralCostpointChanges(updates.costpoints));

      console.log('[DraftTransactionList] Is structural change:', isStructuralChange);

      if (isStructuralChange) {
        console.log('[DraftTransactionList] Triggering save due to structural change');
        // Trigger save when structural changes are made - but don't force UI refresh
        setTimeout(() => onSaveDraft(), 1000); // Increased debounce time to 1 second
      } else {
        console.log('[DraftTransactionList] Skipping save - tag-only change');
      }
      // Tag-only updates don't trigger immediate save to avoid UI refresh
    },
    [onUpdateTransaction, onSaveDraft, hasStructuralCostpointChanges]
  );

  const canCommit =
    draft.validation_summary.incomplete_transactions === 0 &&
    draft.validation_summary.total_errors === 0;

  const canPartialCommit = draft.validation_summary.complete_transactions > 0;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Import Draft: {draft.filename}</h1>
            <p className="text-sm text-gray-500 mt-1">
              Created: {new Date(draft.created_at).toLocaleString()}
            </p>
          </div>

          <div className="flex items-center space-x-2">
            <span
              className={`px-3 py-1 rounded-full text-xs font-medium ${
                draft.status === 'draft'
                  ? 'bg-yellow-100 text-yellow-800'
                  : draft.status === 'ready'
                    ? 'bg-green-100 text-green-800'
                    : draft.status === 'validating'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-gray-100 text-gray-800'
              }`}
            >
              {draft.status.toUpperCase()}
            </span>
          </div>
        </div>

        <DraftProgressBar validationSummary={draft.validation_summary} />
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <div className="space-y-4">
          {/* Auto-save and Save Draft */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={autoSaveEnabled}
                  onChange={(e) => setAutoSaveEnabled(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Auto-save</span>
              </label>

              <button
                onClick={onSaveDraft}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {isLoading ? 'Saving...' : 'Save Draft'}
              </button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 flex-1">
              <button
                onClick={onAbandonDraft}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                Abandon
              </button>

              <button
                onClick={onValidateDraft}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-blue-700 bg-white border border-blue-300 rounded-md hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {isLoading ? 'Validating...' : 'Validate'}
              </button>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <button
                onClick={onPartialCommitDraft}
                disabled={!canPartialCommit || isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-600 border border-transparent rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:bg-gray-400"
                title={`Commit ${draft.validation_summary.complete_transactions} completed transaction(s)`}
              >
                {isLoading
                  ? 'Committing...'
                  : `Commit Completed (${draft.validation_summary.complete_transactions})`}
              </button>

              <button
                onClick={onCommitDraft}
                disabled={!canCommit || isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:bg-gray-400"
              >
                {isLoading ? 'Committing...' : 'Commit All'}
              </button>
            </div>
          </div>
        </div>

        {!canCommit && (
          <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-sm text-yellow-800">
              {canPartialCommit
                ? `You can commit ${draft.validation_summary.complete_transactions} completed transaction(s) now, or complete all ${draft.validation_summary.incomplete_transactions} remaining transaction(s) to commit everything at once.`
                : 'Complete at least one transaction to enable partial commit, or complete all transactions to commit everything at once.'}
            </p>
          </div>
        )}
      </div>

      {/* Multi-select Controls */}
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={
                  selectedTransactions.size === draft.transactions.length &&
                  draft.transactions.length > 0
                }
                onChange={handleSelectAll}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">
                Select All ({selectedTransactions.size}/{draft.transactions.length})
              </span>
            </label>

            {selectedTransactions.size > 0 && (
              <button
                onClick={handleClearSelection}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Clear Selection
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Bulk Actions Toolbar */}
      {showBulkActions && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-blue-900">
                {selectedTransactions.size} transaction
                {selectedTransactions.size !== 1 ? 's' : ''} selected
              </span>
            </div>
            <div className="flex items-center space-x-3">
              <button
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => setShowBulkEditModal(true)}
                disabled={bulkEditing}
              >
                {bulkEditing ? 'Applying Changes...' : 'Bulk Edit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transaction List */}
      <div className="space-y-4">
        {draft.transactions.map((transaction) => (
          <DraftTransactionItem
            key={transaction.id}
            transaction={transaction}
            isSelected={selectedTransactionId === transaction.id}
            isMultiSelected={selectedTransactions.has(transaction.id)}
            onSelect={() =>
              setSelectedTransactionId(
                selectedTransactionId === transaction.id ? null : transaction.id
              )
            }
            onMultiSelect={() => handleSelectTransaction(transaction.id)}
            onUpdate={(updates) => handleTransactionUpdate(transaction.id, updates)}
            onSplitTransaction={handleSplitTransaction}
          />
        ))}
      </div>

      {/* Summary */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {draft.validation_summary.total_transactions}
            </div>
            <div className="text-sm text-gray-500">Total Transactions</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {draft.validation_summary.complete_transactions}
            </div>
            <div className="text-sm text-gray-500">Complete</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600">
              {draft.validation_summary.incomplete_transactions}
            </div>
            <div className="text-sm text-gray-500">Incomplete</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">
              {draft.validation_summary.total_errors}
            </div>
            <div className="text-sm text-gray-500">Errors</div>
          </div>
        </div>
      </div>

      {/* Bulk Edit Modal */}
      <BulkEditModal
        isOpen={showBulkEditModal}
        onClose={() => setShowBulkEditModal(false)}
        onSave={handleBulkEdit}
        selectedCount={selectedTransactions.size}
        totalAmount={Array.from(selectedTransactions)
          .map((id) => draft.transactions.find((t) => t.id === id))
          .filter((t): t is DraftTransaction => t !== undefined)
          .reduce((sum, t) => sum + Math.abs(t.amount), 0)}
      />
    </div>
  );
};

// Memoize the component to prevent unnecessary re-renders
export const DraftTransactionList = React.memo(DraftTransactionListComponent);
